// Belege anzeigen, drucken, sichern, teilen.
//
// Auf dem Schreibtisch öffnet sich wie bisher ein neues Fenster — dort ist das
// der gewohnte Weg. Auf dem Telefon nicht: ein neues Fenster ist in der App
// entweder gesperrt oder es erscheint eine nackte Seite ohne Zurück. Dort
// kommt der Beleg als Blatt über der App, mit einer eigenen Leiste.
//
// Warum nicht einfach ein <a href>: die Druckansicht liegt hinter der
// Anmeldung, und ein Link kann keinen Authorization-Header mitschicken.
import { token } from '../../../gemeinsam/lib/api';
import { istNativ } from '../../../gemeinsam/lib/plattform';

/** Unter dieser Breite gilt die Blatt-Ansicht — dieselbe Grenze wie überall. */
const SCHMAL = '(max-width: 1023.98px)';

function schmal(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(SCHMAL).matches;
}

/**
 * Lädt einen Beleg und zeigt ihn an.
 * Gibt bei Erfolg null zurück, sonst eine Meldung für den Anwender.
 */
export async function belegOeffnen(pfad: string): Promise<string | null> {
  // Auf schmalen Bildschirmen gar nicht erst versuchen: das Fenster wäre
  // entweder gesperrt oder eine Sackgasse ohne Zurück.
  const alsBlatt = schmal() || istNativ();

  // Das Fenster muss direkt im Klick entstehen — nach dem await wertet der
  // Browser den Aufruf nicht mehr als Nutzeraktion.
  const fenster = alsBlatt ? null : window.open('', '_blank');
  if (fenster) fenster.document.write('<p style="font:14px system-ui;padding:24px">Beleg wird geladen …</p>');

  let html: string;
  try {
    const antwort = await fetch(pfad, { headers: { Authorization: `Bearer ${token() ?? ''}` } });
    if (!antwort.ok) throw new Error(String(antwort.status));
    html = await antwort.text();
  } catch {
    fenster?.close();
    return 'Beleg konnte nicht geladen werden.';
  }

  if (fenster) {
    fenster.document.open();
    fenster.document.write(html);
    fenster.document.close();
    return null;
  }

  alsBlattZeigen(html, pfad);
  return null;
}

/** Titel aus dem Dokument ziehen — für Kopfleiste und Dateiname. */
function titelAus(html: string): string {
  const t = /<title>([^<]*)<\/title>/i.exec(html);
  return (t?.[1] || 'Beleg').trim();
}

/**
 * Das Server-Dokument für die Anzeige auf dem Telefon herrichten.
 *
 * Zwei Eingriffe, beide nötig:
 * - Der eigene Druckknopf des Dokuments fliegt raus. Die Leiste des Blattes
 *   bringt ihre eigenen Knöpfe mit; zwei übereinander verwirren nur.
 * - Das Blatt ist auf 210 mm gebaut, also rund 794 px. Auf 390 px läuft es
 *   seitwärts aus dem Bild. Unter 820 px darf es die volle Breite nutzen.
 */
function fuersTelefon(html: string): string {
  const ohneKnopf = html.replace(/<div class="drucken">[\s\S]*?<\/div>/i, '');
  const zusatz = `
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  @media screen and (max-width: 820px) {
    body { background: #fff; padding: 0; }
    .blatt { max-width: none; width: 100%; margin: 0; padding: 14px 12px;
             box-shadow: none; box-sizing: border-box; }
    /* Die Unterschriftenfelder stehen nebeneinander und sprengen sonst die
       Breite — auf dem Telefon untereinander. */
    .unterschrift { flex-direction: column; gap: 10mm; }
    .zahlcode { flex-direction: column; }
    /* Die Positionstabelle ist für 210 mm gerechnet; auf 390 px fiele die
       Betragsspalte aus dem Bild. Feste Spaltenbreiten, kleinere Schrift,
       und lange Artikelnamen dürfen umbrechen. Die Tabelle des Zahlcodes
       bleibt ausgenommen — sie ist ohnehin schmal. */
    table:not(.zahlcode-angaben) { font-size: 7.5pt; }
    table:not(.zahlcode-angaben) th,
    table:not(.zahlcode-angaben) td { padding: 1.2mm .8mm; }
    /* break-word statt anywhere: nur umbrechen, wenn es nicht anders geht.
       Sonst steht „Edelstahlsc hraube" mitten im Wort auseinandergerissen. */
    table:not(.zahlcode-angaben) td { overflow-wrap: break-word; hyphens: auto; }
    /* Zahlen und Einheiten dürfen nie umbrechen — „3,5 Std." gehört zusammen. */
    table:not(.zahlcode-angaben) td.r { white-space: nowrap; }
    table:not(.zahlcode-angaben) th { letter-spacing: 0; font-size: 7pt; }
    table:not(.zahlcode-angaben) td.nr,
    table:not(.zahlcode-angaben) th:first-child { width: 5mm; padding-right: 0; }
  }
</style>`;
  return ohneKnopf.includes('</head>')
    ? ohneKnopf.replace('</head>', `${zusatz}</head>`)
    : zusatz + ohneKnopf;
}

/** Ein Dateiname ohne Zeichen, die ein Dateisystem nicht mag. */
function dateiname(titel: string): string {
  return titel.replace(/[^\p{L}\p{N}._-]+/gu, '_').replace(/^_+|_+$/g, '').slice(0, 80) || 'Beleg';
}

// Eigene Zeichnungen statt Schriftzeichen: SF-Symbole gibt es nur auf
// Apple-Geräten, auf Android und im Browser blieben leere Kästchen stehen.
const SYMBOLE: Record<string, string> = {
  teilen: '<path d="M12 16V4"/><path d="M8 7l4-3 4 3"/><path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6"/>',
  speichern: '<path d="M12 4v12"/><path d="M8 13l4 3 4-3"/><path d="M5 20h14"/>',
  drucken: '<path d="M7 9V3h10v6"/><rect x="4" y="9" width="16" height="7" rx="2"/><path d="M7 14h10v7H7z"/>',
};

function knopf(beschriftung: string, symbol: keyof typeof SYMBOLE, wirkung: () => void, haupt = false) {
  const b = document.createElement('button');
  b.type = 'button';
  b.innerHTML = `<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
      >${SYMBOLE[symbol]}</svg>`
    + `<span>${beschriftung}</span>`;
  b.style.cssText = 'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:3px;'
    + 'flex:1;min-height:52px;border:0;border-radius:14px;cursor:pointer;'
    + 'font:600 12.5px -apple-system,BlinkMacSystemFont,system-ui,sans-serif;'
    + (haupt ? 'background:#4f46e5;color:#fff;' : 'background:#eef2ff;color:#4338ca;');
  b.onclick = wirkung;
  return b;
}

/** Zeigt ein fertiges Belegdokument als Blatt über der Anwendung. */
function alsBlattZeigen(rohHtml: string, pfad: string) {
  const titel = titelAus(rohHtml);
  const html = fuersTelefon(rohHtml);

  const blatt = document.createElement('div');
  blatt.style.cssText = 'position:fixed;inset:0;z-index:9999;background:#f1f5f9;'
    + 'display:flex;flex-direction:column;'
    // Sichere Zonen: sonst liegen Kopf- und Fußleiste unter Uhr und Home-Balken.
    + 'padding-top:var(--sicher-oben,0px);padding-bottom:var(--sicher-unten,0px);';
  if (document.documentElement.classList.contains('dark')) blatt.style.background = '#020617';

  // --- Kopfleiste --------------------------------------------------------
  const kopf = document.createElement('div');
  kopf.style.cssText = 'display:flex;align-items:center;gap:8px;padding:8px 10px;'
    + 'border-bottom:.5px solid rgba(100,116,139,.35);flex:none;';

  const schliessen = document.createElement('button');
  schliessen.type = 'button';
  schliessen.textContent = 'Schließen';
  schliessen.style.cssText = 'min-height:44px;padding:0 10px;border:0;background:none;cursor:pointer;'
    + 'font:600 16px -apple-system,BlinkMacSystemFont,system-ui,sans-serif;color:#4f46e5;';
  schliessen.onclick = () => blatt.remove();

  const name = document.createElement('p');
  name.textContent = titel;
  name.style.cssText = 'flex:1;margin:0;text-align:center;overflow:hidden;text-overflow:ellipsis;'
    + 'white-space:nowrap;font:600 15px -apple-system,BlinkMacSystemFont,system-ui,sans-serif;'
    + 'color:' + (document.documentElement.classList.contains('dark') ? '#f1f5f9' : '#0f172a') + ';';

  // Gleiche Breite wie „Schließen", damit der Titel wirklich mittig steht.
  const ausgleich = document.createElement('span');
  ausgleich.style.cssText = 'width:92px;flex:none;';

  kopf.append(schliessen, name, ausgleich);

  // --- Der Beleg ---------------------------------------------------------
  const rahmen = document.createElement('iframe');
  rahmen.title = titel;
  rahmen.style.cssText = 'flex:1;width:100%;border:0;background:#fff;';
  rahmen.srcdoc = html;

  // --- Aktionsleiste -----------------------------------------------------
  const leiste = document.createElement('div');
  leiste.style.cssText = 'display:flex;gap:8px;padding:10px 12px 12px;flex:none;'
    + 'border-top:.5px solid rgba(100,116,139,.35);';

  const meldung = document.createElement('p');
  meldung.setAttribute('role', 'status');
  meldung.style.cssText = 'margin:0;padding:0 14px;font:500 13px -apple-system,system-ui,sans-serif;'
    + 'color:#b45309;text-align:center;display:none;';

  const sagen = (text: string) => {
    meldung.textContent = text;
    meldung.style.display = 'block';
    setTimeout(() => { meldung.style.display = 'none'; }, 4000);
  };

  leiste.append(
    knopf('Teilen', 'teilen', () => void mitPdf(pfad, titel, sagen, 'teilen')),
    knopf('Speichern', 'speichern', () => void mitPdf(pfad, titel, sagen, 'speichern')),
    knopf('Drucken', 'drucken', () => void drucken(rahmen, pfad, titel, sagen), true),
  );

  blatt.append(kopf, rahmen, meldung, leiste);
  document.body.appendChild(blatt);
}

/**
 * Holt denselben Beleg als PDF vom Server.
 *
 * Mit Nachweis, dass wirklich ein PDF zurückkam. Ein Server, der `format=pdf`
 * noch nicht kennt, ignoriert den Zusatz und schickt fröhlich die HTML-Ansicht
 * mit Status 200. Ungeprüft landete die dann als „.pdf" im Teilen-Blatt — die
 * Datei heißt PDF, ist aber keines, und kein Programm kann sie öffnen. Genau
 * das ist passiert. Deshalb werden Kopfzeile UND die ersten Bytes geprüft:
 * jedes PDF beginnt mit %PDF-.
 */
async function pdfHolen(pfad: string): Promise<Blob> {
  const trenner = pfad.includes('?') ? '&' : '?';
  const antwort = await fetch(`${pfad}${trenner}format=pdf`, {
    headers: { Authorization: `Bearer ${token() ?? ''}` },
  });
  if (!antwort.ok) throw new KeinPdf(`Server antwortet mit ${antwort.status}.`);

  const art = antwort.headers.get('content-type') || '';
  const blob = await antwort.blob();
  const anfang = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
  const kennung = String.fromCharCode(...anfang);

  if (!art.includes('application/pdf') || kennung !== '%PDF-') {
    throw new KeinPdf('Dieser Server erzeugt noch keine PDF-Belege.');
  }
  return blob;
}

/** Der Server hat etwas geschickt, aber kein PDF. */
class KeinPdf extends Error {}

/** Blob → Base64 ohne Präfix; so will es das Dateisystem der Hülle. */
function alsBase64(blob: Blob): Promise<string> {
  return new Promise((fertig, daneben) => {
    const leser = new FileReader();
    leser.onerror = () => daneben(leser.error);
    leser.onload = () => {
      const roh = String(leser.result);
      fertig(roh.slice(roh.indexOf(',') + 1));
    };
    leser.readAsDataURL(blob);
  });
}

/** PDF in den Zwischenspeicher der App legen und seinen Ort zurückgeben. */
async function pdfAblegen(blob: Blob, titel: string): Promise<string> {
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const pfad = `${dateiname(titel)}.pdf`;
  // Ohne encoding schreibt das Dateisystem Base64 als Binärdatei — genau das
  // braucht ein PDF. Mit UTF-8 käme eine kaputte Datei heraus.
  await Filesystem.writeFile({ path: pfad, data: await alsBase64(blob), directory: Directory.Cache });
  const { uri } = await Filesystem.getUri({ path: pfad, directory: Directory.Cache });
  return uri;
}

/**
 * Teilen und Speichern gehen denselben Weg.
 *
 * Auf dem iPhone führt beides über das Blatt des Systems: dort steht „Teilen"
 * neben „In Dateien sichern" und „Drucken". Ein eigener Sichern-Dialog
 * existiert dort nicht, und einen nachzubauen wäre gegen die Plattform.
 */
async function mitPdf(pfad: string, titel: string, sagen: (t: string) => void,
                      zweck: 'teilen' | 'speichern' | 'drucken') {
  const warte = zweck === 'drucken' ? 'PDF wird vorbereitet …' : 'PDF wird erstellt …';
  sagen(warte);
  let blob: Blob;
  try {
    blob = await pdfHolen(pfad);
  } catch (e) {
    sagen(e instanceof KeinPdf ? e.message : 'PDF konnte nicht geladen werden.');
    return;
  }

  if (istNativ()) {
    try {
      const uri = await pdfAblegen(blob, titel);
      const { Share } = await import('@capacitor/share');
      if (zweck === 'drucken') sagen('Im Blatt „Drucken" wählen.');
      await Share.share({ title: titel, files: [uri] });
    } catch {
      sagen(zweck === 'drucken' ? 'Drucken hat nicht geklappt.' : 'Teilen hat nicht geklappt.');
    }
    return;
  }

  // Im Browser: erst das Teilen-Blatt des Systems, sonst herunterladen.
  const datei = new File([blob], `${dateiname(titel)}.pdf`, { type: 'application/pdf' });
  if (zweck !== 'speichern' && navigator.canShare?.({ files: [datei] })) {
    try { await navigator.share({ title: titel, files: [datei] }); return; } catch { /* abgebrochen */ }
  }
  herunterladen(blob, titel);
}

/**
 * Drucken.
 *
 * Im Browser druckt der eingebettete Rahmen — der gewohnte Weg, und der
 * Ausdruck sieht aus wie die Vorschau.
 *
 * In der Hülle nicht: WKWebView kennt `print()` eines eingebetteten Rahmens
 * nicht und meldet dabei keinen Fehler — es passiert schlicht nichts. Genau
 * das war der tote Knopf. Dort geht es über das Blatt des Systems, in dem
 * „Drucken" steht; gedruckt wird das echte PDF und nicht der Bildschirm.
 */
async function drucken(rahmen: HTMLIFrameElement, pfad: string, titel: string,
                       sagen: (t: string) => void) {
  if (istNativ()) {
    await mitPdf(pfad, titel, sagen, 'drucken');
    return;
  }
  try {
    const f = rahmen.contentWindow;
    if (!f) throw new Error('kein Rahmen');
    f.focus();
    f.print();
  } catch {
    await mitPdf(pfad, titel, sagen, 'drucken');
  }
}

function herunterladen(blob: Blob, titel: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${dateiname(titel)}.pdf`;
  a.click();
  // Erst freigeben, wenn der Browser den Download angenommen hat.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
